import * as Yup from 'yup';
import { Box } from '@material-ui/core';
import { Formik } from 'formik';
import { SBase, SETVIImage } from '@setvi/shared/components';
import { FC } from 'react';
import { useQuoteConfig } from 'pages/quotes/hooks/use-quote-config';
import { SPOFormFields } from './spo-form-fields';

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .max(100, 'Limit is 100 characters')
    .required('Product name is required'),
  vendorName: Yup.string()
    .max(500, 'Limit is 500 characters')
    .required('Vendor name is required'),
  sku: Yup.string()
    .max(100, 'Limit is 100 characters')
    .required('SKU is required'),
  price: Yup.number()
    .required('Price is required')
    .min(1, 'Price must be greater than 0')
});

interface SPOFormValues {
  name: string;
  vendorName: string;
  sku: string;
  price: number | null;
  unitOfMeasure: string;
}

export interface SPOSubmitData {
  name: string;
  sku: string;
  sell: number;
  cost: number;
  vendorName: string;
  unitOfMeasure?: string;
}

interface AddSPOProps {
  onClose: () => void;
  onSubmit: (data: SPOSubmitData) => Promise<any>;
  isAddingProducts: boolean;
}

export const AddSPO: FC<AddSPOProps> = ({
  onClose,
  onSubmit,
  isAddingProducts
}) => {
  const { quoteConfig } = useQuoteConfig({});

  const handleSubmit = async (value: SPOFormValues) => {
    const profitMarginPercentage = quoteConfig?.marginPercentage || 0;

    const cost = Number(value?.price);
    const margin = Number(profitMarginPercentage);
    const sellPerUnit = cost / (1 - margin / 100);

    await onSubmit({
      name: value.name,
      sku: value.sku,
      sell: sellPerUnit,
      cost,
      vendorName: value.vendorName,
      unitOfMeasure: value.unitOfMeasure || undefined
    });
  };

  return (
    <Formik
      initialValues={{
        name: '',
        vendorName: '',
        sku: '',
        price: null,
        unitOfMeasure: ''
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        await handleSubmit(values);
        onClose?.();
        resetForm();
      }}>
      {formik => (
        <SBase
          header={{ title: 'Add SPO', onClose }}
          footer={{
            onConfirm: () => formik.handleSubmit(),
            onClose,
            confirmLabel: 'Add Product',
            closeLabel: 'Cancel',
            disableConfirm:
              !formik.isValid || !formik.dirty || isAddingProducts,
            confirmLoading: isAddingProducts
          }}
          width={1024}
          height={768}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            style={{ gap: 32 }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}>
              <SETVIImage
                width="80%"
                src="/images/percentage-spo-illustration.png"
                alt="Add SPO"
              />
            </Box>
            <Box flex={1}>
              <SPOFormFields formik={formik} />
            </Box>
          </Box>
        </SBase>
      )}
    </Formik>
  );
};
